import { handleActions } from 'redux-actions';
import { Actions, cacheCliFailure, cacheCliRequest, cacheCliSuccess, deleteUploadFailure, sqlRequest, sqlSuccess, uploadFailure } from 'actions/adminActions';
import { merge, Dictionary } from 'lodash';
import { Upload, SqlResults, CacheId } from 'shared/models';

export type State = {
    sql: {
        sql: string | null;
        pending: boolean;
        results: SqlResults[] | null;
        err: Error | null;
    };
    cacheCli: {
        pending: boolean;
        commands: { cacheId: CacheId; cmd: string }[];
        results: string[];
        errorMessage: string | null;
    };
    uploads: {
        loading: boolean;
        data: Dictionary<Upload>;
    };
    upload: {
        progress?: number;
        error: string | null;
    } | null;
};

export const initialState = Object.freeze<State>({
    sql: {
        sql: null,
        pending: false,
        results: null,
        err: null,
    },
    cacheCli: {
        pending: false,
        commands: [],
        results: [],
        errorMessage: null,
    },
    uploads: {
        loading: false,
        data: {},
    },
    upload: null,
});

export const adminReducer = handleActions({
    [Actions.SQL_REQUEST]: (state: State, action: ReturnType<typeof sqlRequest>) => {
        return merge({}, state, {
            sql: {
                sql: action.payload.sql,
                pending: true,
                err: null,
            },
        });
    },
    [Actions.SQL_SUCCESS]: (state: State, action: ReturnType<typeof sqlSuccess>) => {
        return {
            ...state,
            sql: {
                ...state.sql,
                pending: false,
                results: action.payload,
            },
        };
    },
    [Actions.SQL_FAILURE]: (state: State, action: any) => {
        return merge({}, state, { sql: { pending: false, err: action.payload } });
    },

    [Actions.CACHE_CLI_REQUEST]: (state: State, action: ReturnType<typeof cacheCliRequest>) => {
        const command = action.payload;
        const { cacheId } = action.meta;
        const { commands } = state.cacheCli;
        return {
            ...state,
            cacheCli: {
                ...state.cacheCli,
                commands: [
                    ...commands.filter(c => c.cmd !== command || c.cacheId !== cacheId).splice(-9), // take 9 most-recent, non-duplicate entries
                    { cacheId, cmd: command },
                ],
                pending: true,
            },
        };
    },
    [Actions.CACHE_CLI_SUCCESS]: (state: State, action: ReturnType<typeof cacheCliSuccess>) => {
        return {
            ...state,
            cacheCli: {
                ...state.cacheCli,
                pending: false,
                results: action.payload,
                errorMessage: null,
            },
        };
    },
    [Actions.CACHE_CLI_FAILURE]: (state: State, action: ReturnType<typeof cacheCliFailure>) => {
        return merge({}, state, { cacheCli: { pending: false, errorMessage: action.payload } });
    },

    [Actions.GET_UPLOADS_REQUEST]: (state: State, action: any) => {
        return merge({}, state, { uploads: { loading: true } });
    },
    [Actions.GET_UPLOADS_SUCCESS]: (state: State, action: any) => {
        return merge({}, state, { uploads: {
            loading: false,
            data: action.payload,
        } });
    },
    [Actions.GET_UPLOADS_FAILURE]: (state: State, action: any) => {
        return merge({}, state, { uploads: {
            loading: false,
            error: action.payload,
        } });
    },

    [Actions.UPLOAD_REQUEST]: (state: State, action: any) => {
        return merge({}, state, { upload: { progress: 0 } });
    },
    [Actions.UPLOAD_PROGRESS]: (state: State, action: any) => {
        return merge({}, state, { upload: { progress: action.payload } });
    },
    [Actions.UPLOAD_SUCCESS]: (state: State, action: any) => {
        const { filename } = action.payload;
        return merge({}, state, {
            upload: null,
            uploads: {
                data: {
                    [filename]: action.payload,
                },
            },
        });
    },
    [Actions.UPLOAD_FAILURE]: (state: State, action: ReturnType<typeof uploadFailure>) => {
        return {
            ...state,
            upload: {
                error: action.payload,
            },
        };
    },
    [Actions.DISMISS_UPLOAD_ERROR]: (state: State, action: any) => {
        return { ...state, upload: null };
    },

    //[Actions.DELETE_UPLOAD_REQUEST]: (state: State, action: any) => {
    //    Nothing to do
    //},
    [Actions.DELETE_UPLOAD_EARLY]: (state: State, action: any) => {
        // Optimistically remove file when the request happens
        const data = { ...state.uploads.data };
        delete data[action.payload];
        return {
            ...state,
            uploads: {
                ...state.uploads,
                data,
            },
        };
    },
    //[Actions.DELETE_UPLOAD_SUCCESS]: (state: State, action: any) => {
    //    Nothing to do
    //},
    [Actions.DELETE_UPLOAD_FAILURE]: (state: State, action: ReturnType<typeof deleteUploadFailure>) => {
        // Reverse the premature file removal
        const { upload } = action.meta;
        return merge({}, state, { uploads: { data: { [upload.filename]: upload } } });
    },
} as any, initialState);
